import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { UseQueryResult } from '@tanstack/react-query'
import { usePortalQuery, usePortalMutation } from 'src/Libs/api'
import {
  getWarrantyInfo,
  revokeWarranty as revokeWarrantyApi,
  RevokeWarrantyParams,
  updateWarrantyNote as updateWarrantyNoteApi,
  UpdateWarrantyNoteParams,
  WarrantyInfo,
} from 'src/Libs/installation-details/api'

type UpdateWarrantyNoteVariables = Omit<UpdateWarrantyNoteParams, 'authFetch'>
type RevokeWarrantyVariables = Omit<RevokeWarrantyParams, 'authFetch'>

interface UseWarrantyInfoReturnType {
  warrantyInfo: WarrantyInfo | null
  isLoading: boolean
  isError: boolean
  updateWarrantyNote: (vars: UpdateWarrantyNoteVariables) => Promise<void | null>
  isUpdatingWarrantyInfo: boolean
  refetchWarrantyInfo: () => Promise<UseQueryResult>
  revokeWarranty: (vars: RevokeWarrantyVariables) => Promise<void | null>
  isRevokingWarranty: boolean
}

/**
 * Hook to manage warranty info of a specific device link and installation,
 * including fetching the warranty info and updating the warranty note.
 */
export const useWarrantyInfo = (
  installationId: string | undefined,
  deviceLinkId: number | undefined,
  statementId: number | undefined
): UseWarrantyInfoReturnType => {
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()

  const { data, isLoading, isError, refetch } = usePortalQuery({
    queryKey: ['warranty-info', installationId, deviceLinkId, statementId],
    queryFn: () => getWarrantyInfo(installationId, deviceLinkId, statementId, authFetch),
    enabled: !!installationId && !!deviceLinkId && !!statementId,
    staleTime: 0,
  })

  const { mutateAsync, isPending } = usePortalMutation({
    mutationFn: (vars: UpdateWarrantyNoteVariables) =>
      updateWarrantyNoteApi({
        ...vars,
        authFetch,
      }),
    onSuccess: () => {
      enqueueSuccessStack(t('system-details.installation-statements.informations.change-warranty.success'))
    },
    onError: (err: Error | undefined) => {
      console.error('Error updating warranty note', err)
      enqueueErrorStack(t('system-details.installation-statements.informations.change-warranty.error'))
    },
  })

  const { mutateAsync: revokeWarranty, isPending: isRevokingWarranty } = usePortalMutation({
    mutationFn: (vars: RevokeWarrantyVariables) =>
      revokeWarrantyApi({
        ...vars,
        authFetch,
      }),
    onSuccess: () => {
      enqueueSuccessStack(t('system-details.installation-statements.informations.revoke-warranty.success'))
    },
    onError: (err: Error | undefined) => {
      console.error('Error updating warranty note', err)
      enqueueErrorStack(t('system-details.installation-statements.informations.revoke-warranty.error'))
    },
  })

  return {
    warrantyInfo: (data as WarrantyInfo | null) ?? null,
    refetchWarrantyInfo: refetch,
    isLoading,
    isError,
    updateWarrantyNote: mutateAsync,
    isUpdatingWarrantyInfo: isPending,
    revokeWarranty,
    isRevokingWarranty,
  }
}
